import PropTypes from "prop-types";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import useGetUserWallet from "../../../hooks/wallet/useGetUserWallet";
import {useGetPaykuBanks, usePostPaykuWithdrawal} from "../../../hooks/wallet/usePostPaykuWithdrawal";
import useCurrencyStore from "../../../store/useCurrency.store";
import {formatCurrencyAmount} from "../../../util/balance.util";
import styles from "./PaykuWithdrawal.module.css";

// Account type options for Chile
const CHILE_ACCOUNT_TYPES = {
  1: "Cuenta Corriente",
  2: "Cuenta Vista/RUT",
  3: "Cuenta de Ahorro",
};

// Chilean RUT validation
const validateChileanRUT = rut => {
  if (!rut) return false;

  // Remove dots and dashes, keep only numbers and K
  const cleanRUT = rut.replace(/[^0-9kK]/g, "");

  if (cleanRUT.length < 8 || cleanRUT.length > 9) {
    return false;
  }

  // Basic format validation
  return /^\d{7,8}[0-9kK]$/i.test(cleanRUT);
};

const PaykuWithdrawal = ({onBack, onCancel}) => {
  const {t} = useTranslation();
  const {selectedCurrency} = useCurrencyStore();
  const {getAccountByCurrency} = useGetUserWallet();

  const [formData, setFormData] = useState({
    amount: "",
    beneficiaryName: "",
    email: "",
    phone: "",
    accountbank_name: "",
    accountbank_rut: "",
    accountbank_sbif: "",
    accountbank_type: 2,
    accountbank_num: "",
  });

  const [feedback, setFeedback] = useState(null);

  const {mutate: createWithdrawal, isPending, isError, error} = usePostPaykuWithdrawal();
  const {data: banksData, isLoading: banksLoading} = useGetPaykuBanks("CLP");

  const isValidCurrency = selectedCurrency?.id === "CLP";
  const currentCurrencyAccount = selectedCurrency ? getAccountByCurrency(selectedCurrency.id) : null;

  if (!isValidCurrency) {
    return (
      <div className={styles.unsupportedContainer}>
        <h3 className={styles.unsupportedTitle}>{t("withdrawalPaykuUnsupportedCurrency")}</h3>
        <p className={styles.unsupportedMessage}>{t("withdrawalPaykuOnlyCLP")}</p>
        <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
      </div>
    );
  }

  const availableBalance = currentCurrencyAccount?.balance || 0;
  const formattedBalance = formatCurrencyAmount(availableBalance, "CLP", "$");

  const validateForm = () => {
    const errors = [];

    if (!formData.amount || Number(formData.amount) < 1000) {
      errors.push(t("withdrawalPaykuMinAmount"));
    }

    if (Number(formData.amount) > availableBalance) {
      errors.push(t("withdrawalPaykuInsufficientBalance"));
    }

    if (!formData.beneficiaryName.trim()) {
      errors.push(t("withdrawalPaykuBeneficiaryNameRequired"));
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(t("withdrawalPaykuValidEmail"));
    }

    if (!formData.phone.trim()) {
      errors.push(t("withdrawalPaykuPhoneRequired"));
    }

    if (!validateChileanRUT(formData.accountbank_rut)) {
      errors.push(t("withdrawalPaykuValidRUT"));
    }

    if (!formData.accountbank_sbif) {
      errors.push(t("withdrawalPaykuBankRequired"));
    }

    if (!formData.accountbank_num.trim()) {
      errors.push(t("withdrawalPaykuAccountNumberRequired"));
    }

    return errors;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBankSelect = e => {
    const bankCode = e.target.value;
    const selectedBank = banksData?.data?.find(bank => bank.code === bankCode);
    if (selectedBank) {
      setFormData(prev => ({
        ...prev,
        accountbank_sbif: bankCode,
        accountbank_name: selectedBank.name,
      }));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      setFeedback({type: "error", text: validationErrors[0]});
      return;
    }

    setFeedback(null);

    const withdrawalData = {
      ...formData,
      amount: Number(formData.amount),
      currency: "CLP",
    };

    createWithdrawal(withdrawalData, {
      onSuccess: response => {
        if (response.data?.success) {
          setFeedback({type: "success", text: t("withdrawalPaykuSuccess")});
          setTimeout(() => {
            if (onCancel) onCancel();
          }, 2000);
        } else {
          setFeedback({type: "error", text: response.data?.message || t("withdrawalPaykuError")});
        }
      },
      onError: err => {
        setFeedback({type: "error", text: err.message || t("withdrawalPaykuError")});
      },
    });
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{t("withdrawalPaykuTitle")}</h2>
      <p className={styles.balanceInfo}>
        {t("withdrawalPaykuAvailableBalance")}: <span className={styles.balanceAmount}>{formattedBalance}</span>
      </p>

      <form onSubmit={handleSubmit}>
        <div className={styles.formGrid}>
          {/* Amount */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("withdrawalPaykuAmount")} <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputWithSuffix}>
              <input
                className={styles.input}
                type="number"
                value={formData.amount}
                onChange={e => handleInputChange("amount", e.target.value)}
                placeholder={t("withdrawalPaykuEnterAmount")}
                min={1000}
                max={availableBalance}
              />
              <span className={styles.inputSuffix}>CLP</span>
            </div>
            <p className={styles.fieldHint}>{t("withdrawalPaykuMinAmount")}: 1,000 CLP</p>
          </div>

          {/* Beneficiary Name */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("withdrawalPaykuBeneficiaryName")} <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              value={formData.beneficiaryName}
              onChange={e => handleInputChange("beneficiaryName", e.target.value)}
              placeholder={t("withdrawalPaykuBeneficiaryNamePlaceholder")}
            />
          </div>

          {/* Email & Phone in two columns */}
          <div className={styles.twoColumns}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuEmail")} <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                type="email"
                value={formData.email}
                onChange={e => handleInputChange("email", e.target.value)}
                placeholder={t("withdrawalPaykuEmailPlaceholder")}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuPhone")} <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                value={formData.phone}
                onChange={e => handleInputChange("phone", e.target.value)}
                placeholder={t("withdrawalPaykuPhonePlaceholder")}
              />
            </div>
          </div>

          {/* RUT */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("withdrawalPaykuRUT")} <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              value={formData.accountbank_rut}
              onChange={e => handleInputChange("accountbank_rut", e.target.value)}
              placeholder={t("withdrawalPaykuRUTPlaceholder")}
            />
            <p className={styles.fieldHint}>{t("withdrawalPaykuRUTFormat")}</p>
          </div>

          {/* Bank & Account Type in two columns */}
          <div className={styles.twoColumns}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuBank")} <span className={styles.required}>*</span>
              </label>
              <select
                className={styles.select}
                value={formData.accountbank_sbif}
                onChange={handleBankSelect}
                disabled={banksLoading}
              >
                <option value="">{t("withdrawalPaykuSelectBank")}</option>
                {banksData?.data?.map(bank => (
                  <option
                    key={bank.code}
                    value={bank.code}
                  >
                    {bank.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                {t("withdrawalPaykuAccountType")} <span className={styles.required}>*</span>
              </label>
              <select
                className={styles.select}
                value={formData.accountbank_type}
                onChange={e => handleInputChange("accountbank_type", Number(e.target.value))}
              >
                {Object.entries(CHILE_ACCOUNT_TYPES).map(([value, label]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Account Number */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {t("withdrawalPaykuAccountNumber")} <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              value={formData.accountbank_num}
              onChange={e => handleInputChange("accountbank_num", e.target.value)}
              placeholder={t("withdrawalPaykuAccountNumberPlaceholder")}
            />
          </div>
        </div>

        {feedback && (
          <div className={feedback.type === "error" ? styles.errorFeedback : styles.successFeedback}>
            {feedback.text}
          </div>
        )}

        {isError && error && !feedback && (
          <div className={styles.errorFeedback}>{error.message || t("withdrawalPaykuError")}</div>
        )}

        <div className={styles.actionsBar}>
          <PrimaryButton
            type="submit"
            disabled={isPending}
            fullWidth
          >
            {isPending ? "Procesando..." : t("withdrawalPaykuSubmit")}
          </PrimaryButton>
          <div className={styles.secondaryRow}>
            <SecondaryButton onClick={onBack}>{t("depositInstructionsBack")}</SecondaryButton>
            <SecondaryButton onClick={onCancel}>{t("depositInstructionsCancel")}</SecondaryButton>
          </div>
        </div>
      </form>
    </div>
  );
};

PaykuWithdrawal.propTypes = {
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default PaykuWithdrawal;
